// libraries
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

// ant-design
import { Button, Drawer, Form, FormInstance, Input, List, notification, Tooltip, Typography } from 'antd';
import { CheckOutlined, DeleteFilled, PlusOutlined } from '@ant-design/icons';

// utils
import { txt } from '../../../locales/i18n';

// services
import * as WordTrackService from '../../../services/bb-api/word-track';

export type WordTrackDrawerProps = {
	useVisible: [boolean, (state: boolean) => void];
	words: Record<string, any>[];
	getWords: () => Promise<Record<string, any>[]>;
};

export function WordTrackListDrawer(props: WordTrackDrawerProps): JSX.Element {
	const [visible, setVisible] = props.useVisible;
	const [loading, setLoading] = useState<boolean>(false);
	const [words, setWords] = useState<Record<string, any>[]>([]);
	const [search, setSearch] = useState<string>('');

	const location = useLocation();

	useEffect(() => {
		setWords(props.words);
	}, [props.words]);

	useEffect(() => {
		if (search === '') return setWords(props.words);

		const filteredWords: Record<string, any>[] = props.words.filter((word: Record<string, any>) => {
			return word.word.includes(search);
		});

		setWords(filteredWords);
	}, [search, props.words]);

	function handleClose(): void {
		setVisible(false);
		setWords(props.words);
		setSearch('');
	}

	function getHeaderTitle(): string {
		const hash: string = location.hash;
		if (hash === '#groups') return txt('menu.groups');
		return 'Boblberg';
	}

	return (
		<StyledDrawer
			title={getHeaderTitle()}
			placement="right"
			width="24rem"
			onClose={handleClose}
			visible={visible}
			destroyOnClose
			footer={<DrawerFooter getWords={props.getWords} setLoading={setLoading} />}
		>
			<List
				loading={loading}
				header={<ListHeader useSearch={[search, setSearch]} />}
				dataSource={words}
				renderItem={(word: Record<string, any>) => (
					<List.Item key={word.id}>
						<WordItem word={word} getWords={props.getWords} setLoading={setLoading} />
					</List.Item>
				)}
			/>
		</StyledDrawer>
	);
}

export type ListHeaderProps = {
	useSearch: [string, (state: string) => void];
};

function ListHeader({ useSearch }: ListHeaderProps): JSX.Element {
	const [search, setSearch] = useSearch;

	return (
		<div className="list-header-container">
			<Typography.Text>
				<Typography.Text strong type="secondary">
					{txt('word track.tail example')}
				</Typography.Text>{' '}
				{txt('word track.tail info')}
			</Typography.Text>

			<Typography.Text>
				<Typography.Text strong type="secondary">
					{txt('word track.head example')}
				</Typography.Text>{' '}
				{txt('word track.head info')}
			</Typography.Text>

			<Typography.Text>
				<Typography.Text strong type="secondary">
					{txt('word track.head and tail example')}
				</Typography.Text>{' '}
				{txt('word track.head and tail info')}
			</Typography.Text>

			<Input placeholder={txt('global.search')} allowClear value={search} onChange={({ target }) => setSearch(target.value)} />
		</div>
	);
}

export type WordItemProps = {
	word: Record<string, any>;
	getWords: () => Promise<Record<string, any>[]>;
	setLoading: (state: boolean) => void;
};

function WordItem({ word, getWords, setLoading }: WordItemProps): JSX.Element {
	async function handleUpdate(values: any): Promise<void> {
		setLoading(true);

		try {
			await WordTrackService.updateWord(word.id, values);
			notification.success({ message: txt('notification.update successful') });
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setLoading(false);
	}

	async function handleDelete(): Promise<void> {
		setLoading(true);

		try {
			await WordTrackService.removeWord(word.id);
			await getWords();
			notification.success({ message: txt('notification.deletion successful') });
		} catch (error) {
			notification.error({ message: txt('notification.deletion failed') });
		}

		setLoading(false);
	}

	return (
		<Form className="list-item-form-container" onFinish={handleUpdate} initialValues={word}>
			<Form.Item name="word">
				<Input bordered={false} required />
			</Form.Item>

			<Tooltip title={txt('global.update')}>
				<Form.Item>
					<Button type="link" htmlType="submit">
						<CheckOutlined />
					</Button>
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.delete')}>
				<Form.Item>
					<Button danger type="link" onClick={handleDelete}>
						<DeleteFilled />
					</Button>
				</Form.Item>
			</Tooltip>
		</Form>
	);
}

type DrawerFooterProps = {
	getWords: () => Promise<Record<string, any>[]>;
	setLoading: (state: boolean) => void;
};

function DrawerFooter({ getWords, setLoading }: DrawerFooterProps): JSX.Element {
	const formRef = useRef<FormInstance>(null);

	async function handleAddWord(values: any): Promise<void> {
		const form: FormInstance | null = formRef.current;
		if (form === null) return;

		setLoading(true);

		try {
			await WordTrackService.createWord(values);
			form.resetFields();
			await getWords();
			notification.success({ message: txt('notification.create successful') });
		} catch (error) {
			notification.error({ message: txt('notification.create failed') });
		}

		setLoading(false);
	}

	return (
		<Form layout="inline" onFinish={handleAddWord} ref={formRef}>
			<Tooltip title={txt('global.word')}>
				<Form.Item name="word">
					<Input bordered={false} required placeholder={txt('global.add word')} />
				</Form.Item>
			</Tooltip>

			<Form.Item>
				<Button htmlType="submit" type="primary">
					{txt('global.create')} <PlusOutlined />
				</Button>
			</Form.Item>
		</Form>
	);
}

const StyledDrawer = styled(Drawer)`
	.ant-drawer-content-wrapper {
		max-width: 100vw;
	}

	.word-inputs-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.word-input-container {
		display: flex;
		gap: 1rem;
	}

	.ant-drawer-footer > .ant-form {
		display: flex;
		justify-content: space-between;
	}

	.list-header-container {
		display: flex;
		flex-direction: column;
		gap: 0.6rem;
	}

	.list-item-form-container {
		display: flex;
	}
`;
